import React from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTimer } from "./TimerContext";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  loadStatusTimeouts as loadTimeouts,
  getStatusTimeout as getTimeout,
} from "../../config/orderStatusConfig";

interface OrderStatusProgressProps {
  status: string;
  startTime?: string;
  timeoutMinutes: number;
  showLabel?: boolean;
}

/**
 * Get timeout for status (delegates to centralized config)
 */
export const getStatusTimeout = (status: string): number | null => {
  return getTimeout(status);
};

/**
 * Preload timeouts on app start (delegates to centralized config)
 */
export const preloadStatusTimeouts = () => {
  loadTimeouts();
};

/**
 * Виджет прогресс-бара таймаута статуса заказа с обновлением в реальном времени
 */
export const OrderStatusProgress: React.FC<OrderStatusProgressProps> = ({
  status,
  startTime,
  timeoutMinutes,
  showLabel = true,
}) => {
  const { t } = useTranslation();
  const { tick } = useTimer();
  void tick;

  if (!startTime) {
    // Вместо возврата null, отображаем компонент с информацией о недоступности данных
    return (
      <Box sx={{ width: "100%", mt: 1, mb: 1 }}>
        {showLabel && (
          <Typography variant="caption" color="text.disabled">
            {t("timer_unavailable")}
          </Typography>
        )}
        <LinearProgress
          variant="determinate"
          value={0}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "grey.200",
            opacity: 0.5,
            "& .MuiLinearProgress-bar": {
              bgcolor: "grey.400",
              borderRadius: 4,
            },
          }}
        />
      </Box>
    );
  }

  const start = new Date(startTime).getTime();
  const now = Date.now();
  const timeout = timeoutMinutes * 60 * 1000;

  const elapsed = now - start;
  const remaining = timeout - elapsed;
  const progress = Math.min(elapsed / timeout, 1);

  // Определение цвета и статуса
  let color: string;
  let statusText: string;
  let icon: React.ReactNode;

  if (remaining < 0) {
    color = "#d32f2f";
    statusText = t("timer_overdue");
    icon = <ErrorIcon fontSize="small" />;
  } else {
    const remainingPercent = remaining / timeout;
    if (remainingPercent < 0.1) {
      color = "#d32f2f";
      statusText = t("timer_critical");
      icon = <ErrorIcon fontSize="small" />;
    } else if (remainingPercent < 0.5) {
      color = "#f57c00";
      statusText = t("timer_warning");
      icon = <WarningIcon fontSize="small" />;
    } else {
      color = "#2e7d32";
      statusText = t("timer_normal");
      icon = <CheckCircleIcon fontSize="small" />;
    }
  }

  const formatRemaining = (ms: number) => {
    if (ms < 0) return t("timer_overdue");
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);

    if (hours > 0) {
      return t("time_format_hours", { hours, minutes });
    } else if (minutes > 0) {
      return t("time_format_minutes", { minutes, seconds });
    } else {
      return t("time_format_seconds", { seconds });
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 1, mb: 1 }}>
      {showLabel && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            {icon}
            <Typography variant="caption" fontWeight={600} sx={{ color }}>
              {statusText}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t("timer_remaining", { time: formatRemaining(remaining) })}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
        <LinearProgress
          variant="determinate"
          value={progress * 100}
          sx={{
            height: 10,
            borderRadius: 12,
            bgcolor: "white",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            "& .MuiLinearProgress-bar": {
              bgcolor: color,
              borderRadius: 12,
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
            },
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * Получение времени начала для статуса
 */
export const getStatusStartTime = (order: any): string | undefined => {
  switch (order.status) {
    case "pending":
      return order.created_at; // Время создания заказа
    case "waiting_approval":
      return order.assigned_at; // Время отклика исполнителя
    case "assigned":
      return order.assigned_at;
    case "in_progress":
      return order.work_started_at;
    case "completed":
      return order.work_completed_at;
    default:
      return undefined;
  }
};

export default OrderStatusProgress;

// Prevent tree-shaking
if (typeof window !== "undefined") {
  (window as any).__OrderStatusProgress = OrderStatusProgress;
  (window as any).__getStatusTimeout = getStatusTimeout;
  (window as any).__getStatusStartTime = getStatusStartTime;
}
